import { CustomApiResponse } from "utils/helper/ApiResponses";
import { Account, JournalEntry } from "utils/types/Accounting.types";
import { ApiService } from "../apiservices";
import { apiSrcUrl } from "../ApiRoutes";

/**
 * Fetches the full Chart of Accounts from the backend.
 * @param token The JWT token for authorization.
 * @returns A promise that resolves to the list of accounts.
 */
export async function getChartOfAccounts(token: string): Promise<Account[]> {
  const response = await ApiService.get<CustomApiResponse<Account[]>>(
    `${apiSrcUrl}/private/accounting/chart-of-accounts`,
    { Authorization: `Bearer ${token}` }
  );
  if (response.data.status === 'success' && response.data.data) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to fetch chart of accounts");
}

/**
 * Submits a new manual journal entry to the backend.
 * @param entryData The journal entry object (without the ID).
 * @param token The JWT token for authorization.
 * @returns A promise that resolves to the response containing the new entry's ID.
 */
export async function createManualJournalEntry(
  entryData: Omit<JournalEntry, 'id'>,
  token: string
): Promise<{ id: string }> {
  const response = await ApiService.post<CustomApiResponse<{ id: string }>>(
    `${apiSrcUrl}/private/accounting/journal-entries`,
    entryData,
    { Authorization: `Bearer ${token}` }
  );

  if (response.data.status === 'success' && response.data.data) {
    return response.data.data;
  }
  // The backend might send a 'fail' status, which we should throw as an error
  throw new Error(response.data.message || "Failed to create journal entry");
}